import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
const TITLE = "حاسبة وزن المعادن - حساب وزن الحديد والصاج والاستانلس والألمنيوم";
const DESCRIPTION =
  "حاسبة وزن المعادن المجانية - احسب وزن الحديد، الصاج، الاستانلس ستيل، الألمنيوم، والنحاس بدقة. أدخل الأبعاد واحصل على الوزن فوراً. مثالية للمواسير، الألواح، القضبان، والزوايا.";
const KEYWORDS =
  "حاسبة وزن الحديد, حاسبة وزن الصاج, حساب وزن المعادن, وزن الاستانلس, حاسبة المواسير, حساب وزن الألمنيوم, وزن القضبان, حاسبة الزوايا الحديدية, steel weight calculator";
const OG_URL = "https://do-calculate.com/calculator/ar/metal-weight/";
// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة وزن المعادن",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة وزن المعادن",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كيف أحسب وزن الصاج؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "لحساب وزن الصاج، أدخل الطول والعرض والسُمك في الحاسبة، ثم اختر نوع المعدن (حديد، استانلس، ألمنيوم). الحاسبة ستحسب الوزن تلقائياً بناءً على الكثافة.",
        },
      },
      {
        "@type": "Question",
        name: "ما هي كثافة الحديد؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "كثافة الحديد الكربوني العادي هي 7.85 جم/سم³، بينما كثافة الاستانلس ستيل 8.00 جم/سم³، والألمنيوم 2.70 جم/سم³.",
        },
      },
    ],
  },
];
// Material densities (g/cm³)
const DENSITIES = {
  steel: { value: 7.85, name: "حديد كربوني" },
  stainless: { value: 8.0, name: "استانلس ستيل" },
  aluminium: { value: 2.7, name: "ألمنيوم" },
  copper: { value: 8.96, name: "نحاس" },
  brass: { value: 8.5, name: "نحاس أصفر (برونز)" },
  zinc: { value: 7.14, name: "زنك" },
};
// Profile types
const PROFILE_TYPES = {
  plate: "لوح / صاج",
  round: "قضيب دائري",
  pipe: "ماسورة / أنبوب",
  square: "قضيب مربع",
  rectangular: "قضيب مستطيل",
  angle: "زاوية",
  square_tube: "ماسورة مربعة",
  rectangular_tube: "ماسورة مستطيلة",
  hex: "قضيب سداسي",
  ibeam: "كمرة I",
};
function MetalWeightCalculator() {
  // States
  const [profileType, setProfileType] = useState("plate");
  const [material, setMaterial] = useState("steel");
  const [customDensity, setCustomDensity] = useState("");
  const [unit, setUnit] = useState("mm");
  const [quantity, setQuantity] = useState(1);
  // Dimensions (all in selected unit)
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [thickness, setThickness] = useState("");
  const [diameter, setDiameter] = useState("");
  const [outerDiameter, setOuterDiameter] = useState("");
  const [innerDiameter, setInnerDiameter] = useState("");
  const [height, setHeight] = useState("");
  const [leg1, setLeg1] = useState("");
  const [leg2, setLeg2] = useState("");
  const [flangeWidth, setFlangeWidth] = useState("");
  const [flangeThickness, setFlangeThickness] = useState("");
  const [webHeight, setWebHeight] = useState("");
  const [webThickness, setWebThickness] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // Unit conversion factors to mm
  const unitFactors = {
    mm: 1,
    cm: 10,
    m: 1000,
    inch: 25.4,
  };
  // Convert to mm
  const toMm = (value) => {
    return parseFloat(value || 0) * unitFactors[unit];
  };
  // Calculate weight
  const calculateWeight = () => {
    try {
      setError("");
      // Get density
      const density = material === "custom"
        ? parseFloat(customDensity || 0)
        : DENSITIES[material].value;
      if (density <= 0) {
        setError("يرجى إدخال كثافة صحيحة");
        setResult(null);
        return;
      }
      let volumeCm3 = 0;
      // Calculate volume based on profile type
      switch (profileType) {
        case "plate":
          // Volume = length × width × thickness
          const plateLength = toMm(length) / 10; // convert to cm
          const plateWidth = toMm(width) / 10;
          const plateThickness = toMm(thickness) / 10;
          volumeCm3 = plateLength * plateWidth * plateThickness;
          break;
        case "round":
          // Volume = π × r² × length
          const roundDiameter = toMm(diameter) / 10;
          const roundLength = toMm(length) / 10;
          const radius = roundDiameter / 2;
          volumeCm3 = Math.PI * radius * radius * roundLength;
          break;
        case "pipe":
          // Volume = π × (R² - r²) × length
          const pipeOuterD = toMm(outerDiameter) / 10;
          const pipeInnerD = toMm(innerDiameter) / 10;
          const pipeLength = toMm(length) / 10;
          const outerR = pipeOuterD / 2;
          const innerR = pipeInnerD / 2;
          volumeCm3 = Math.PI * (outerR * outerR - innerR * innerR) * pipeLength;
          break;
        case "square":
          // Volume = side × side × length
          const squareSide = toMm(width) / 10;
          const squareLength = toMm(length) / 10;
          volumeCm3 = squareSide * squareSide * squareLength;
          break;
        case "rectangular":
          // Volume = width × height × length
          const rectWidth = toMm(width) / 10;
          const rectHeight = toMm(height) / 10;
          const rectLength = toMm(length) / 10;
          volumeCm3 = rectWidth * rectHeight * rectLength;
          break;
        case "angle":
          // Volume = (leg1 × thickness + leg2 × thickness - thickness²) × length
          const angleLeg1 = toMm(leg1) / 10;
          const angleLeg2 = toMm(leg2) / 10;
          const angleThickness = toMm(thickness) / 10;
          const angleLength = toMm(length) / 10;
          const crossSection = (angleLeg1 * angleThickness) +
            (angleLeg2 * angleThickness) -
            (angleThickness * angleThickness);
          volumeCm3 = crossSection * angleLength;
          break;
        case "square_tube":
          // Volume = (outer² - inner²) × length
          const sqTubeOuter = toMm(width) / 10;
          const sqTubeThickness = toMm(thickness) / 10;
          const sqTubeInner = sqTubeOuter - 2 * sqTubeThickness;
          const sqTubeLength = toMm(length) / 10;
          volumeCm3 = (sqTubeOuter * sqTubeOuter - sqTubeInner * sqTubeInner) * sqTubeLength;
          break;
        case "rectangular_tube":
          // Volume = (outerWidth × outerHeight - innerWidth × innerHeight) × length
          const rectTubeWidth = toMm(width) / 10;
          const rectTubeHeight = toMm(height) / 10;
          const rectTubeThickness = toMm(thickness) / 10;
          const rectTubeInnerWidth = rectTubeWidth - 2 * rectTubeThickness;
          const rectTubeInnerHeight = rectTubeHeight - 2 * rectTubeThickness;
          const rectTubeLength = toMm(length) / 10;
          volumeCm3 = (rectTubeWidth * rectTubeHeight -
            rectTubeInnerWidth * rectTubeInnerHeight) * rectTubeLength;
          break;
        case "hex":
          // Volume = (√3/2) × side² × length (for regular hexagon)
          const hexSide = toMm(width) / 10;
          const hexLength = toMm(length) / 10;
          volumeCm3 = (Math.sqrt(3) / 2) * hexSide * hexSide * hexLength;
          break;
        case "ibeam":
          // Volume ≈ (2 × flangeWidth × flangeThickness + webHeight × webThickness) × length
          const iFlangeWidth = toMm(flangeWidth) / 10;
          const iFlangeThickness = toMm(flangeThickness) / 10;
          const iWebHeight = toMm(webHeight) / 10;
          const iWebThickness = toMm(webThickness) / 10;
          const iLength = toMm(length) / 10;
          const iCrossSection = (2 * iFlangeWidth * iFlangeThickness) +
            (iWebHeight * iWebThickness);
          volumeCm3 = iCrossSection * iLength;
          break;
        default:
          throw new Error("نوع القطاع غير معروف");
      }
      // Weight = Volume × Density
      const weightKg = (volumeCm3 * density) / 1000; // Convert g to kg
      const totalWeight = weightKg * quantity;
      // Calculate additional information
      const weightTon = totalWeight / 1000;
      const weightGram = totalWeight * 1000;
      const weightPerMeter = weightKg / (toMm(length) / 1000);
      setResult({
        weightPerPiece: weightKg,
        totalWeight,
        weightTon,
        weightGram,
        weightPerMeter,
        volumeCm3,
        density,
        materialName: material === "custom" ? "مخصص" : DENSITIES[material].name,
        profileName: PROFILE_TYPES[profileType],
      });
    } catch (err) {
      setError("خطأ في الحساب: " + err.message);
      setResult(null);
    }
  };
  useEffect(() => {
    if (length && (width || diameter || outerDiameter)) {
      calculateWeight();
    }
  }, [
    profileType,
    material,
    customDensity,
    unit,
    quantity,
    length,
    width,
    thickness,
    diameter,
    outerDiameter,
    innerDiameter,
    height,
    leg1,
    leg2,
    flangeWidth,
    flangeThickness,
    webHeight,
    webThickness,
  ]);
  const handleClear = () => {
    setLength("");
    setWidth("");
    setThickness("");
    setDiameter("");
    setOuterDiameter("");
    setInnerDiameter("");
    setHeight("");
    setLeg1("");
    setLeg2("");
    setFlangeWidth("");
    setFlangeThickness("");
    setWebHeight("");
    setWebThickness("");
    setQuantity(1);
    setResult(null);
    setError("");
  };
  // Render input fields based on profile type
  const renderInputFields = () => {
    switch (profileType) {
      case "plate":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >العرض ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="العرض"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >السُمك ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="السُمك"
                />
              </div>
            </div>
          </>
        );
      case "round":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >القطر ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={diameter}
                  onChange={(e) => setDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="القطر"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "pipe":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >القطر الخارجي ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={outerDiameter}
                  onChange={(e) => setOuterDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="القطر الخارجي"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >القطر الداخلي ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={innerDiameter}
                  onChange={(e) => setInnerDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="القطر الداخلي"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "square":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >طول الضلع ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="طول الضلع"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "rectangular":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >العرض ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="العرض"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >الارتفاع ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الارتفاع"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "angle":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-3">
                <label >الضلع الأول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={leg1}
                  onChange={(e) => setLeg1(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الضلع الأول"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >الضلع الثاني ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={leg2}
                  onChange={(e) => setLeg2(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الضلع الثاني"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >السُمك ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="السُمك"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "square_tube":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >العرض الخارجي ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="العرض الخارجي"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >سُمك الجدار ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="سُمك الجدار"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "rectangular_tube":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-3">
                <label >العرض ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="العرض"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >الارتفاع ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الارتفاع"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >سُمك الجدار ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="سُمك الجدار"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "hex":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >طول الضلع ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="طول الضلع"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      case "ibeam":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >عرض الشفة ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={flangeWidth}
                  onChange={(e) => setFlangeWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="عرض الشفة"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >سُمك الشفة ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={flangeThickness}
                  onChange={(e) => setFlangeThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="سُمك الشفة"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >ارتفاع الجسم ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={webHeight}
                  onChange={(e) => setWebHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="ارتفاع الجسم"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >سُمك الجسم ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={webThickness}
                  onChange={(e) => setWebThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="سُمك الجسم"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >الطول ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="الطول"
                />
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة وزن المعادن</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة وزن المعادن</strong> أداة مجانية ودقيقة تساعدك
                على <strong>حساب وزن</strong> الحديد، الصاج، الاستانلس ستيل،
                الألمنيوم، والنحاس بسهولة. أدخل أبعاد القطعة واختر نوع المعدن
                للحصول على الوزن الدقيق فوراً. مثالية للمواسير، الألواح،
                القضبان، الزوايا، والكمرات. استخدمها للتسعير، حساب الشحن، أو
                التخطيط للمشاريع الهندسية والبناء.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  احسب وزن المعدن بسهولة
                </h2>
              </div>
              {/* Profile Type Selection */}
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="profileType" >
                    نوع القطاع
                  </label>
                  <select
                    id="profileType"
                    className="form-select"
                    value={profileType}
                    onChange={(e) => setProfileType(e.target.value)}
                  >
                    {Object.entries(PROFILE_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Material Selection */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="material" >
                    نوع المعدن
                  </label>
                  <select
                    id="material"
                    className="form-select"
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                  >
                    {Object.entries(DENSITIES).map(([key, { name, value }]) => (
                      <option key={key} value={key}>
                        {name} ({value} جم/سم³)
                      </option>
                    ))}
                    <option value="custom">كثافة مخصصة</option>
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="unit" >
                    وحدة القياس
                  </label>
                  <select
                    id="unit"
                    className="form-select"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="mm">مليمتر (mm)</option>
                    <option value="cm">سنتيمتر (cm)</option>
                    <option value="m">متر (m)</option>
                    <option value="inch">بوصة (inch)</option>
                  </select>
                </div>
              </div>
              {/* Custom Density */}
              {material === "custom" && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="customDensity" >
                      الكثافة المخصصة (جم/سم³)
                    </label>
                    <input
                      id="customDensity"
                      type="number"
                      className="form-control"
                      value={customDensity}
                      onChange={(e) => setCustomDensity(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="أدخل الكثافة"
                      style={{ backgroundColor: "#fff3cd" }}
                    />
                  </div>
                </div>
              )}
              {/* Dynamic Input Fields */}
              {renderInputFields()}
              {/* Quantity */}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="quantity" >
                    الكمية (عدد القطع)
                  </label>
                  <input
                    id="quantity"
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    min="1"
                    placeholder="الكمية"
                  />
                </div>
              </div>
              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  امسح
                </button>
              </div>
              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}
              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            البيان
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            وزن القطعة الواحدة
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {result.weightPerPiece.toFixed(4)} كجم
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            الوزن الإجمالي ({quantity} قطعة)
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            {result.totalWeight.toFixed(4)} كجم
                          </td>
                        </tr>
                        {result.weightPerMeter > 0 && (
                          <tr>
                            <td style={{ textAlign: "center" }}>
                              الوزن لكل متر
                            </td>
                            <td style={{ textAlign: "center", fontSize: "16px" }}>
                              {result.weightPerMeter.toFixed(4)} كجم/م
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تنسيقات إضافية للوزن</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            ⚖️ <strong>بالطن:</strong>{" "}
                            {result.weightTon.toFixed(6)} طن
                          </li>
                          <li>
                            ⚖️ <strong>بالجرام:</strong>{" "}
                            {result.weightGram.toFixed(2)} جم
                          </li>
                          <li>
                            📦 <strong>الحجم:</strong>{" "}
                            {result.volumeCm3.toFixed(2)} سم³
                          </li>
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 معلومة:</strong> الكثافة المستخدمة:{" "}
                          {result.density} جم/سم³ ({result.materialName})
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>نوع القطاع:</strong> {result.profileName}
                          </p>
                          <p>
                            <strong>نوع المعدن:</strong> {result.materialName}
                          </p>
                          <p>
                            <strong>الكثافة:</strong> {result.density} جم/سم³
                          </p>
                          <p>
                            <strong>الحجم:</strong> {result.volumeCm3.toFixed(2)} سم³
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>طريقة الحساب:</strong> الوزن = الحجم × الكثافة
                          </p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            الحجم = {result.volumeCm3.toFixed(2)} سم³
                            <br />
                            الكثافة = {result.density} جم/سم³
                            <br />
                            الوزن = {(result.volumeCm3 * result.density).toFixed(2)} جم
                            = {result.weightPerPiece.toFixed(4)} كجم
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة وزن المعادن؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>حاسبة وزن المعادن</strong> هي أداة إلكترونية مجانية
                  تساعدك على حساب وزن أي قطعة معدنية بدقة عالية بناءً على أبعادها
                  ونوع المعدن. تتيح لك الحاسبة:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حساب وزن أنواع مختلفة:</strong> حديد، استانلس،
                    ألمنيوم، نحاس، برونز، زنك
                  </li>
                  <li>
                    <strong>دعم أشكال متعددة:</strong> ألواح، مواسير، قضبان،
                    زوايا، كمرات
                  </li>
                  <li>
                    <strong>وحدات قياس مرنة:</strong> مليمتر، سنتيمتر، متر، بوصة
                  </li>
                  <li>
                    <strong>حساب الكمية:</strong> احسب وزن عدة قطع دفعة واحدة
                  </li>
                  <li>
                    <strong>كثافة مخصصة:</strong> أدخل كثافة خاصة لمعادن أخرى
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام الحاسبة:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر نوع القطاع (لوح، ماسورة، قضيب، إلخ)</li>
                  <li>اختر نوع المعدن والوحدة المناسبة</li>
                  <li>أدخل الأبعاد المطلوبة حسب نوع القطاع</li>
                  <li>أدخل عدد القطع إذا كنت تريد حساب وزن أكثر من قطعة</li>
                  <li>ستظهر النتيجة تلقائياً بمجرد إدخال البيانات</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 ملاحظة:</strong> جميع الحسابات تستخدم الكثافة
                  القياسية لكل معدن لضمان دقة النتائج. يمكنك استخدام خيار "كثافة
                  مخصصة" للمعادن غير المدرجة أو للسبائك الخاصة.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات حاسبة وزن المعادن</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  في البناء والتشييد:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حساب كميات الحديد:</strong> معرفة وزن حديد التسليح
                    للمشاريع
                  </li>
                  <li>
                    <strong>تقدير تكاليف المواد:</strong> حساب تكلفة الحديد
                    والصاج
                  </li>
                  <li>
                    <strong>التخطيط اللوجستي:</strong> معرفة أوزان الشحنات
                    والنقل
                  </li>
                  <li>
                    <strong>الكمرات والهياكل:</strong> حساب وزن الكمرات الحديدية
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في الورش والمصانع:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تسعير المنتجات:</strong> حساب تكلفة المواد الخام
                  </li>
                  <li>
                    <strong>إدارة المخزون:</strong> معرفة أوزان المواد المخزنة
                  </li>
                  <li>
                    <strong>طلبات الشراء:</strong> تحديد الكميات المطلوبة بدقة
                  </li>
                  <li>
                    <strong>مراقبة الإنتاج:</strong> حساب استهلاك المواد
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في التجارة والتوريد:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تسعير المبيعات:</strong> حساب سعر البيع حسب الوزن
                  </li>
                  <li>
                    <strong>عروض الأسعار:</strong> إعداد عروض دقيقة للعملاء
                  </li>
                  <li>
                    <strong>حساب الشحن:</strong> معرفة تكاليف النقل والشحن
                  </li>
                  <li>
                    <strong>مقارنة الموردين:</strong> مقارنة الأسعار بناءً على
                    الوزن
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في الهندسة والتصميم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التصميم الإنشائي:</strong> حساب أحمال المباني
                  </li>
                  <li>
                    <strong>تصميم الآلات:</strong> معرفة أوزان المكونات
                  </li>
                  <li>
                    <strong>اختيار المواد:</strong> المفاضلة بين المواد المختلفة
                  </li>
                  <li>
                    <strong>الدراسات الفنية:</strong> إعداد التقارير الفنية
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>كثافات المعادن الشائعة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الكثافة هي كتلة المادة في وحدة الحجم، وتُقاس بالجرام لكل سنتيمتر
                  مكعب (جم/سم³). إليك كثافات المعادن الشائعة:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        المعدن
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        الكثافة (جم/سم³)
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        الاستخدامات
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>حديد كربوني</td>
                      <td style={{ textAlign: "center" }}>7.85</td>
                      <td style={{ textAlign: "center" }}>البناء، حديد التسليح</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>استانلس ستيل</td>
                      <td style={{ textAlign: "center" }}>8.00</td>
                      <td style={{ textAlign: "center" }}>المطابخ، الأدوات الطبية</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>ألمنيوم</td>
                      <td style={{ textAlign: "center" }}>2.70</td>
                      <td style={{ textAlign: "center" }}>النوافذ، الطائرات</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>نحاس</td>
                      <td style={{ textAlign: "center" }}>8.96</td>
                      <td style={{ textAlign: "center" }}>الأسلاك الكهربائية</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>نحاس أصفر (برونز)</td>
                      <td style={{ textAlign: "center" }}>8.50</td>
                      <td style={{ textAlign: "center" }}>الصنابير، الأدوات</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>زنك</td>
                      <td style={{ textAlign: "center" }}>7.14</td>
                      <td style={{ textAlign: "center" }}>الجلفنة، البطاريات</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  لماذا تختلف الكثافات؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  تعتمد كثافة المعدن على:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>التركيب الذري للمعدن</li>
                  <li>طريقة ترتيب الذرات في البنية البلورية</li>
                  <li>وجود شوائب أو عناصر سبائكية</li>
                  <li>درجة الحرارة والضغط</li>
                </ul>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ فائدة:</strong> الألمنيوم أخف بكثير من الحديد (تقريباً
                  ثلث الوزن)، لذلك يُستخدم في التطبيقات التي تتطلب خفة الوزن مثل
                  الطائرات والسيارات.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على حساب الوزن</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: حساب وزن لوح صاج
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> لديك لوح صاج حديدي بأبعاد 2 متر × 1
                  متر × 3 ملم. كم يزن؟
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "لوح / صاج" كنوع القطاع</li>
                  <li>اختر "حديد كربوني" كنوع المعدن</li>
                  <li>أدخل: الطول = 2000 ملم، العرض = 1000 ملم، السُمك = 3 ملم</li>
                  <li>النتيجة: حوالي 47.1 كجم</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب:</strong> الحجم = 200 × 100 × 0.3 = 6000 سم³
                  <br />
                  الوزن = 6000 × 7.85 = 47,100 جم = 47.1 كجم
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 2: حساب وزن ماسورة
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> ماسورة استانلس بقطر خارجي 50 ملم
                  وقطر داخلي 44 ملم وطول 6 متر. كم يزن؟
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "ماسورة / أنبوب"</li>
                  <li>اختر "استانلس ستيل"</li>
                  <li>أدخل: قطر خارجي = 50 ملم، قطر داخلي = 44 ملم، طول = 6000 ملم</li>
                  <li>النتيجة: حوالي 11.07 كجم</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 3: حساب وزن قضيب دائري
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> قضيب ألمنيوم دائري قطره 20 ملم وطوله
                  3 متر. كم يزن؟
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر "قضيب دائري"</li>
                  <li>اختر "ألمنيوم"</li>
                  <li>أدخل: قطر = 20 ملم، طول = 3000 ملم</li>
                  <li>النتيجة: حوالي 2.54 كجم</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  مثال 4: حساب وزن زاوية حديدية
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>المشكلة:</strong> زاوية حديدية 50×50 ملم بسُمك 5 ملم
                  وطول 6 متر. كم تزن؟
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الحل:</strong> اختر "زاوية"، وأدخل الأبعاد، النتيجة:
                  حوالي 22.81 كجم
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح لاستخدام حاسبة الوزن بفعالية</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح عامة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>استخدم نفس الوحدة:</strong> تأكد من إدخال جميع
                    الأبعاد بنفس الوحدة لتجنب الأخطاء
                  </li>
                  <li>
                    <strong>قِس بدقة:</strong> استخدم أدوات قياس دقيقة للحصول على
                    نتائج صحيحة
                  </li>
                  <li>
                    <strong>راجع النتائج:</strong> تحقق من النتائج بفحص قسم
                    التفاصيل
                  </li>
                  <li>
                    <strong>احفظ الحسابات:</strong> التقط صورة للشاشة أو اكتب
                    النتائج للرجوع إليها
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  لتجنب الأخطاء:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>تأكد من اختيار نوع القطاع الصحيح</li>
                  <li>اختر نوع المعدن المناسب</li>
                  <li>أدخل القطر الداخلي أصغر من القطر الخارجي للمواسير</li>
                  <li>تحقق من وحدة القياس قبل الإدخال</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  للاستخدام الاحترافي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>احسب وزن عينة واحدة أولاً ثم اضرب في الكمية</li>
                  <li>أضف هامش 3-5% للتسامحات التصنيعية</li>
                  <li>احتفظ بجدول بالأوزان المعتادة للرجوع السريع</li>
                  <li>استخدم الوزن لكل متر للتخطيط المالي</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ تحذير:</strong> الأوزان المحسوبة هي أوزان نظرية
                  بناءً على الكثافة القياسية. قد يختلف الوزن الفعلي قليلاً بسبب
                  التسامحات التصنيعية أو الشوائب.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>معلومات مثيرة عن المعادن</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  حقائق ممتعة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الحديد في جسمك:</strong> يحتوي جسم الإنسان على حوالي
                    4 جرامات من الحديد، معظمها في الدم!
                  </li>
                  <li>
                    <strong>برج إيفل:</strong> يزن حوالي 10,000 طن من الحديد،
                    ويُعاد طلاؤه كل 7 سنوات
                  </li>
                  <li>
                    <strong>الألمنيوم:</strong> كان أغلى من الذهب في القرن
                    التاسع عشر لصعوبة استخراجه
                  </li>
                  <li>
                    <strong>النحاس:</strong> أول معدن استخدمه الإنسان منذ أكثر
                    من 10,000 سنة
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  أرقام مثيرة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>الإنتاج العالمي من الحديد: حوالي 1.8 مليار طن سنوياً</li>
                  <li>كل 40 ثانية، يُنتَج طن من الألمنيوم في العالم</li>
                  <li>الاستانلس ستيل لا يصدأ لاحتوائه على 10% كروم على الأقل</li>
                  <li>الألمنيوم يمكن إعادة تدويره بلا حدود دون فقدان جودته</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  في العصر الحديث:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>ناطحات السحاب:</strong> تستخدم آلاف الأطنان من
                    الحديد والصلب
                  </li>
                  <li>
                    <strong>السيارات:</strong> تحتوي السيارة الحديثة على حوالي
                    900 كجم من الحديد
                  </li>
                  <li>
                    <strong>الطائرات:</strong> 80% من هيكل الطائرة من الألمنيوم
                  </li>
                  <li>
                    <strong>الكوابل:</strong> أكثر من 60% من إنتاج النحاس
                    يُستخدم في الأسلاك
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحسب وزن الصاج؟</h3>
              </div>
              <div className="info-section-body">
                لحساب وزن الصاج:
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>اختر "لوح / صاج" من قائمة نوع القطاع</li>
                  <li>اختر نوع المعدن (حديد، استانلس، ألمنيوم)</li>
                  <li>اختر وحدة القياس المناسبة (ملم، سم، متر)</li>
                  <li>أدخل الطول والعرض والسُمك</li>
                  <li>ستظهر النتيجة تلقائياً بالكيلوجرام</li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>مثال:</strong> صاج حديدي 2م × 1م × 3ملم = حوالي 47.1
                  كجم
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما هي كثافة الحديد؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  كثافة الحديد الكربوني العادي هي <strong>7.85 جم/سم³</strong>.
                  هذا يعني أن كل سنتيمتر مكعب من الحديد يزن 7.85 جرام.
                </p>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>كثافات معادن أخرى:</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>استانلس ستيل: 8.00 جم/سم³</li>
                  <li>ألمنيوم: 2.70 جم/سم³</li>
                  <li>نحاس: 8.96 جم/سم³</li>
                  <li>نحاس أصفر: 8.50 جم/سم³</li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  هذه القيم هي المستخدمة في الحاسبة لضمان دقة النتائج.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كيف أحسب وزن ماسورة حديد؟</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  لحساب وزن ماسورة (أنبوب مجوف):
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>اختر "ماسورة / أنبوب" من قائمة القطاعات</li>
                  <li>اختر نوع المعدن</li>
                  <li>أدخل القطر الخارجي للماسورة</li>
                  <li>أدخل القطر الداخلي (أو احسبه: خارجي - 2×السُمك)</li>
                  <li>أدخل الطول</li>
                  <li>الحاسبة ستحسب حجم المعدن الفعلي (بدون الفراغ الداخلي)</li>
                </ol>
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  <strong>مثال:</strong> ماسورة حديدية قطر خارجي 50 ملم، قطر
                  داخلي 44 ملم (سُمك 3 ملم)، طول 6 متر = حوالي 10.99 كجم
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  لماذا يختلف وزن الألمنيوم عن الحديد؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يختلف الوزن بسبب اختلاف <strong>الكثافة</strong>:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>الحديد:</strong> كثافته 7.85 جم/سم³
                  </li>
                  <li>
                    <strong>الألمنيوم:</strong> كثافته 2.70 جم/سم³
                  </li>
                </ul>
                <p style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  هذا يعني أن الألمنيوم <strong>أخف بحوالي 3 مرات</strong> من
                  الحديد! قطعة ألمنيوم بنفس حجم قطعة حديد ستزن فقط حوالي ثلث
                  وزنها.
                </p>
                <div
                  className="alert alert-success mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>فائدة:</strong> لهذا السبب يُستخدم الألمنيوم في
                  الطائرات والسيارات حيث خفة الوزن مهمة جداً، رغم أن الحديد
                  أقوى وأرخص.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل يمكنني حساب وزن أكثر من قطعة؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>نعم!</strong> الحاسبة تدعم حساب وزن عدة قطع:
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>أدخل أبعاد قطعة واحدة كالمعتاد</li>
                  <li>في حقل "الكمية (عدد القطع)"، أدخل العدد المطلوب</li>
                  <li>
                    ستظهر النتيجة شاملة:
                    <ul style={{ marginTop: "5px" }}>
                      <li>وزن القطعة الواحدة</li>
                      <li>الوزن الإجمالي لجميع القطع</li>
                    </ul>
                  </li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>مثال:</strong> إذا كنت تريد حساب وزن 50 لوح صاج، أدخل
                  أبعاد لوح واحد ثم اكتب 50 في خانة الكمية.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ماذا لو كان معدني غير موجود في القائمة؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  إذا كان المعدن الذي تريده غير موجود في القائمة، يمكنك استخدام
                  خيار <strong>"كثافة مخصصة"</strong>:
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>اختر "كثافة مخصصة" من قائمة نوع المعدن</li>
                  <li>سيظهر حقل جديد لإدخال الكثافة</li>
                  <li>أدخل كثافة المعدن بالجرام لكل سنتيمتر مكعب (جم/سم³)</li>
                  <li>أكمل إدخال الأبعاد كالمعتاد</li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>أين أجد كثافة المعدن؟</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>ابحث عنها في Google أو مراجع المواد الهندسية</li>
                  <li>اطلبها من المورّد أو الشركة المصنّعة</li>
                  <li>راجع المواصفات الفنية للمنتج</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل النتائج دقيقة 100%؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  الحاسبة توفر <strong>نتائج نظرية دقيقة جداً</strong> بناءً على:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>الأبعاد المدخلة</li>
                  <li>الكثافة القياسية للمعدن</li>
                  <li>معادلات حساب الحجم الصحيحة</li>
                </ul>
                <p style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <strong>لكن قد يكون هناك فرق بسيط</strong> (عادة أقل من 2-3%)
                  بين الوزن المحسوب والوزن الفعلي بسبب:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>التسامحات التصنيعية في الأبعاد</li>
                  <li>اختلافات طفيفة في الكثافة حسب التركيب الكيميائي</li>
                  <li>وجود شوائب أو عناصر سبائكية</li>
                  <li>المعالجات الحرارية</li>
                </ul>
                <div
                  className="alert alert-warning mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>نصيحة:</strong> للأعمال التجارية، أضف هامش أمان 3-5%
                  على الوزن المحسوب لتغطية هذه الفروقات.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل يمكنني استخدام الحاسبة على الهاتف؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>بالتأكيد!</strong> الحاسبة مصممة للعمل بكفاءة على جميع
                  الأجهزة:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>📱 الهواتف الذكية:</strong> واجهة متجاوبة ومريحة
                    للشاشات الصغيرة
                  </li>
                  <li>
                    <strong>📲 الأجهزة اللوحية:</strong> تجربة مثالية مع مساحة
                    أكبر
                  </li>
                  <li>
                    <strong>💻 أجهزة الكمبيوتر:</strong> واجهة كاملة مع جميع
                    المزايا
                  </li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  الحاسبة تعمل على أي متصفح حديث (Chrome، Safari، Firefox، Edge)
                  وتحسب النتائج فوراً بدون الحاجة لأي تطبيقات إضافية.
                </p>
                <div
                  className="alert alert-info mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>💡 نصيحة:</strong> يمكنك استخدام الحاسبة في الورشة أو
                  المصنع مباشرة من هاتفك أثناء العمل!
                </div>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default MetalWeightCalculator;